/**
 * Tag the open Outlook email with a category once it has been submitted as an
 * Engage job, mirroring the prototype's addEmailCategory: ensure the master
 * category exists on the mailbox, then apply it to the current item so the user
 * can see at a glance which emails have already been converted to jobs.
 *
 * Outlook-only and best-effort — every path resolves to a boolean and never
 * throws, so a tagging failure (missing permission, non-Outlook host, transient
 * Office error) never affects the job submission that just succeeded.
 */

/** Default category applied to a submitted email. */
export const ENGAGE_SUBMITTED_CATEGORY = "Submitted to Engage";

/**
 * Backoff delays between apply attempts. Classic Outlook desktop needs time to
 * propagate a freshly created master category before it can be applied to an
 * item — an immediate `item.categories.addAsync` with the new name fails.
 */
const APPLY_RETRY_DELAYS_MS: readonly number[] = [500, 1000, 2000];

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Promisify an Office `getAsync`/`addAsync`-style callback. */
function officeAsync<T>(fn: (cb: (r: Office.AsyncResult<T>) => void) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    fn((r) => {
      if (r.status === Office.AsyncResultStatus.Succeeded) resolve(r.value);
      else reject(r.error ?? new Error("Office async call failed"));
    });
  });
}

/** Create the master category (once per mailbox) if it doesn't already exist. */
async function ensureMasterCategory(
  master: Office.MasterCategories | undefined,
  name: string,
): Promise<void> {
  if (!master || typeof master.getAsync !== "function") return;
  // Classic Outlook desktop reports an empty list as null rather than [].
  const existing =
    (await officeAsync<Office.CategoryDetails[] | null>((cb) => master.getAsync(cb))) ?? [];
  if (existing.some((c) => c.displayName === name)) return;
  await officeAsync((cb) =>
    master.addAsync(
      [{ displayName: name, color: Office.MailboxEnums.CategoryColor.Preset9 }],
      cb,
    ),
  );
}

/** Apply the category to the item unless it's already tagged with it. */
async function applyCategory(categories: Office.Categories, name: string): Promise<void> {
  // Classic Outlook desktop returns null (not []) when the item has no
  // categories yet — the common case for the email being tagged.
  const existing =
    (await officeAsync<Office.CategoryDetails[] | null>((cb) => categories.getAsync(cb))) ?? [];
  if (existing.some((c) => c.displayName === name)) return;
  await officeAsync((cb) => categories.addAsync([name], cb));
}

/**
 * Tag the currently open email with `categoryName`. Returns true when the tag
 * was applied (or already present), false when categories aren't available
 * (non-Outlook host, no item, missing ReadWriteMailbox permission, etc.).
 */
export async function tagEmailSubmitted(
  categoryName: string = ENGAGE_SUBMITTED_CATEGORY,
): Promise<boolean> {
  const mailbox = globalThis.Office?.context?.mailbox;
  const item = mailbox?.item as Office.MessageRead | undefined;
  const categories = item?.categories;
  if (!mailbox || !item || !categories || typeof categories.addAsync !== "function") {
    return false;
  }
  try {
    await ensureMasterCategory(mailbox.masterCategories, categoryName);
  } catch (err) {
    // Best-effort — the job was already created; a tag failure is non-fatal.
    console.warn(`Engage: creating master category "${categoryName}" failed`, err);
    return false;
  }
  // Applying a just-created master category can fail in classic Outlook until
  // the mailbox has propagated it, so retry with backoff before giving up.
  // applyCategory re-checks the item first, so a retry after a write that
  // actually landed is still a no-op.
  for (let attempt = 0; ; attempt++) {
    try {
      await applyCategory(categories, categoryName);
      return true;
    } catch (err) {
      const wait = APPLY_RETRY_DELAYS_MS[attempt];
      if (wait === undefined) {
        console.warn(`Engage: applying category "${categoryName}" failed after retries`, err);
        return false;
      }
      await delay(wait); // NOSONAR retry attempts must run one after another
    }
  }
}
